import { Inject, Injectable, PLATFORM_ID, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { CanonicalService } from './canonical.service';
import { LinksService } from './links.service';

export interface SeoConfig {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  structuredData?: any;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private renderer: Renderer2;

  constructor(
    private titleService: Title,
    private meta: Meta,
    private canonicalService: CanonicalService,
    private linksService: LinksService,
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  public setSeo(config: SeoConfig): void {
    const siteName = 'Card4Games';
    const baseUrl = this.linksService.URLApp || 'https://card4games.com';
    const pageTitle = config.title ? `${config.title} | ${siteName}` : siteName;
    const pageDescription = config.description || 'Card4Games - Digital Cards & Game Subscriptions Store';
    const pageImage = config.image || `${baseUrl}/assets/logo-web-2.png`;
    const pageUrl = config.url || baseUrl;
    const pageType = config.type || 'website';

    // 1. Title
    this.titleService.setTitle(pageTitle);

    // 2. Standard Meta Tags
    this.meta.updateTag({ name: 'description', content: pageDescription });
    if (config.keywords) {
      this.meta.updateTag({ name: 'keywords', content: config.keywords });
    }

    // 3. Open Graph Tags
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: pageDescription });
    this.meta.updateTag({ property: 'og:image', content: pageImage });
    this.meta.updateTag({ property: 'og:url', content: pageUrl });
    this.meta.updateTag({ property: 'og:type', content: pageType });
    this.meta.updateTag({ property: 'og:site_name', content: siteName });

    // 4. Twitter Card Tags
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: pageDescription });
    this.meta.updateTag({ name: 'twitter:image', content: pageImage });

    // 5. Canonical Link
    this.canonicalService.setCanonicalUrl(pageUrl);

    // 6. Structured Data (JSON-LD)
    if (config.structuredData) {
      this.setStructuredData(config.structuredData);
    }
  }

  public setStructuredData(data: any): void {
    const existing = this.document.head.querySelector('script[type="application/ld+json"]');
    if (existing) {
      existing.remove();
    }

    const script = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'type', 'application/ld+json');
    this.renderer.setProperty(script, 'text', JSON.stringify(data));
    this.renderer.appendChild(this.document.head, script);
  }
}
